import React, { useState, useMemo, useEffect } from "react";
import {
  Activity,
  Shield,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Briefcase,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import Modal from "../common/Modal";
import Toast, { type ToastMessage } from "../common/Toast";
import { ControlInsightsSkeleton } from "../common/InsightsSkeleton";
import controlRegisterService, { ControlRegister } from "../../services/controlRegisterService";
import NoDataFound from "../common/NoDataFound";
import { usePermission, NOT_AUTHORISED_MSG } from "../../contexts/PermissionContext";
import ControlConfigModal from "./ControlConfigModal";
import { swalError } from "../common/confirm";

// =============================================================================
// Types
// =============================================================================

type ControlRow = {
  id: number;
  code: string;
  name: string;
  category: string;
  controlType: string;
  existence: number;
  effectiveness: number;
  net: number;
  signal: string;
  hasScore: boolean;
};

// =============================================================================
// Helpers
// =============================================================================

const getNetColor = (score: number) => {
  // Standard RAG heatmap (inverted for controls: high score = strong = green).
  if (score >= 20) return "bg-green-700 hover:bg-green-800 border-green-800";
  if (score >= 15) return "bg-lime-500 hover:bg-lime-600 border-lime-600";
  if (score >= 8)  return "bg-yellow-400 hover:bg-yellow-500 border-yellow-500";
  if (score >= 4)  return "bg-orange-500 hover:bg-orange-600 border-orange-600";
  return "bg-red-600 hover:bg-red-700 border-red-700";
};

const getNetTextColor = (score: number) => {
  if (score >= 8 && score < 20) return "text-slate-900";
  return "text-white";
};

const getNetBadgeColor = (score: number) => {
  if (score >= 20) return "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (score >= 15) return "bg-green-100 text-green-700 border-green-200";
  if (score >= 8)  return "bg-yellow-100 text-yellow-700 border-yellow-200";
  if (score >= 4)  return "bg-orange-100 text-orange-700 border-orange-200";
  return "bg-red-100 text-red-700 border-red-200";
};

const getSignalColor = (signal: string) => {
  if (signal === "HEALTHY") return "bg-emerald-100 text-emerald-700";
  if (signal === "WARNING") return "bg-yellow-100 text-yellow-700";
  if (signal === "BREACH")  return "bg-red-100 text-red-700";
  return "bg-slate-100 text-slate-500";
};

// =============================================================================
// Legend
// =============================================================================

const CTR_LEGEND = [
  { label: 'Poor',   cls: 'bg-red-600' },
  { label: 'Weak',   cls: 'bg-orange-500' },
  { label: 'Mod',    cls: 'bg-yellow-400' },
  { label: 'Good',   cls: 'bg-lime-500' },
  { label: 'Strong', cls: 'bg-green-700' },
];

// =============================================================================
// Sub-components
// =============================================================================

const KpiCard = ({ label, value, sub, icon: Icon, iconBg, iconColor }: any) => (
  <div className="relative bg-white rounded-2xl flex items-center gap-4 px-5 py-5 cursor-default overflow-hidden"
    style={{ border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)', transition: 'box-shadow 0.2s ease, transform 0.2s ease' }}
    onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 28px rgba(0,0,0,0.11), 0 2px 6px rgba(0,0,0,0.06)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-1px)'; }}
    onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)'; }}>
    {iconColor && <div className="absolute bottom-0 right-0 w-24 h-24 rounded-full pointer-events-none" style={{ background: `radial-gradient(circle, ${iconColor}0d 0%, transparent 70%)`, transform: 'translate(30%, 30%)' }} />}
    <div className="shrink-0 p-3 rounded-xl" style={{ background: iconBg, boxShadow: `0 2px 10px ${iconColor}28` }}>
      <Icon size={20} style={{ color: iconColor }} />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 truncate mb-1">{label}</p>
      <span className="text-[2rem] font-extrabold text-slate-800 leading-none tabular-nums">{value}</span>
      {sub && <p className="text-[10px] text-slate-400 mt-1 truncate">{sub}</p>}
    </div>
  </div>
);

const DIST_FALLBACK_COLORS = ['#6366f1','#10b981','#f59e0b','#ef4444','#3b82f6','#8b5cf6','#06b6d4','#94a3b8'];

const DistributionWidget = ({ title, data, icon: Icon, total, accentColor = '#6366f1', className = '' }: any) => {
  const maxCount = Math.max(...data.map((d: any) => d.count), 1);
  return (
    <div className={`rounded-2xl overflow-hidden flex flex-col ${className}`}
      style={{ background: '#ffffff', border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)' }}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-white">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg" style={{ background: `${accentColor}12` }}>
            <Icon size={12} style={{ color: accentColor }} />
          </div>
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{title}</span>
        </div>
        {total > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full tabular-nums" style={{ background: `${accentColor}12`, color: accentColor }}>{total}</span>}
      </div>
      {data.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-4 italic">No data.</p>
      ) : (
        <ul className="divide-y divide-slate-50 flex-1">
          {data.map((item: any, idx: number) => {
            const color = item.color || DIST_FALLBACK_COLORS[idx % DIST_FALLBACK_COLORS.length];
            const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
            const barPct = item.count > 0 ? Math.round((item.count / maxCount) * 100) : 0;
            return (
              <li key={idx} className="grid items-center gap-3 px-4 py-2.5 transition-colors duration-100"
                style={{ gridTemplateColumns: '1fr 110px 3.5rem' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide truncate">{item.label}</span>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'linear-gradient(to bottom, #f1f5f9, #e2e8f0)' }}>
                  <div className="h-1.5 rounded-full transition-all duration-500"
                    style={{ width: barPct > 0 ? `${barPct}%` : '0%', background: `linear-gradient(90deg, ${color}bb, ${color})`, boxShadow: barPct > 10 ? `0 1px 4px ${color}55` : 'none' }} />
                </div>
                <div className="flex items-center justify-end gap-1">
                  <span className="text-[12px] font-extrabold tabular-nums" style={{ color: item.count > 0 ? color : '#94a3b8' }}>{item.count}</span>
                  <span className="text-[10px] text-slate-400">({pct}%)</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const ControlMatrix = ({ title, description, icon: Icon, rows, onCellClick }: any) => {
  const matrix = Array(6).fill(null).map(() => Array(6).fill(0));
  rows.forEach((r: ControlRow) => {
    if (!r.hasScore) return;
    const row = 5 - r.effectiveness;
    const col = r.existence;
    if (row >= 0 && row < 6 && col >= 0 && col < 6) matrix[row][col]++;
  });

  return (
    <div className="bg-white p-2.5 rounded-2xl flex flex-col" style={{ border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)' }}>
      {/* header */}
      <div className="flex items-center gap-2 mb-0.5">
        <div className="p-1.5 bg-slate-100 rounded-lg text-slate-600 shrink-0">
          <Icon size={14} />
        </div>
        <div className="min-w-0">
          <h3 className="text-xs font-semibold text-slate-800 leading-tight">{title}</h3>
          <p className="text-[10px] text-slate-400 truncate">{description}</p>
        </div>
      </div>
      {/* legend row */}
      <div className="flex gap-2.5 text-[10px] font-medium text-slate-500 mb-1.5 pl-1">
        {CTR_LEGEND.map(l => (
          <div key={l.label} className="flex items-center gap-1">
            <div className={`w-2 h-2 rounded-sm ${l.cls}`} />
            {l.label}
          </div>
        ))}
      </div>

      <div className="mt-1 flex flex-1">
        <div className="w-6 flex items-center justify-center">
          <span className="text-[10px] font-bold text-slate-400 -rotate-90 whitespace-nowrap tracking-widest uppercase">
            Effectiveness
          </span>
        </div>
        <div className="flex-1">
          <div className="grid gap-1" style={{ gridTemplateRows: 'repeat(6, 48px)', gridTemplateColumns: 'repeat(6, 48px)' }}>
            {matrix.map((row, rowIdx) =>
              row.map((count, colIdx) => {
                const eff = 5 - rowIdx;
                const exi = colIdx;
                const score = eff * exi;
                return (
                  <div
                    key={`${rowIdx}-${colIdx}`}
                    data-testid={`BTN_ctrl_overview_matrix_cell_${eff}_${exi}`}
                    onClick={() => {
                      const cellControls = rows.filter(
                        (r: ControlRow) => r.effectiveness === eff && r.existence === exi,
                      );
                      onCellClick(cellControls, eff, exi);
                    }}
                    className={`relative border rounded-lg flex items-center justify-center cursor-pointer transition-all duration-200 ${getNetColor(score)}`}
                  >
                    {count > 0 && (
                      <span className={`text-sm font-bold ${getNetTextColor(score)}`}>{count}</span>
                    )}
                  </div>
                );
              }),
            )}
          </div>
          <div className="h-6 flex items-center justify-center mt-1">
            <span className="text-[10px] font-bold text-slate-400 tracking-widest uppercase">Existence</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const ControlDetailModal = ({ isOpen, onClose, data, effectiveness, existence, onSelectControl }: any) => (
  <Modal isOpen={isOpen} onClose={onClose} size="lg">
    <Modal.Header>
      <span>
        Control Breakdown
        <span className="block text-sm font-normal text-slate-500 mt-0.5">
          Effectiveness: <span className="font-bold text-slate-700">{effectiveness}</span>{" "}
          • Existence: <span className="font-bold text-slate-700">{existence}</span>
          {" "}• Net Score: <span className="font-bold text-slate-700">{effectiveness * existence}</span>
        </span>
      </span>
    </Modal.Header>
    <Modal.Body>
      {data.length === 0 ? (
        <div className="text-sm text-slate-500 italic py-4">No controls in this cell.</div>
      ) : (
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="text-xs font-bold text-slate-400 uppercase tracking-wider border-b border-slate-200">
              <th className="pb-3 pl-2">Code</th>
              <th className="pb-3">Control Name</th>
              <th className="pb-3">Category</th>
              <th className="pb-3 text-center">Net</th>
              <th className="pb-3 text-center">Signal</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {data.map((ctrl: ControlRow) => (
              <tr key={ctrl.id} data-testid={`ROW_ctrl_overview_detail_${ctrl.id}`}
                onClick={() => onSelectControl(ctrl.id)}
                className="border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer">
                <td className="py-3 pl-2 text-slate-500 text-xs">{ctrl.code}</td>
                <td className="py-3 font-medium text-slate-800">{ctrl.name}</td>
                <td className="py-3 text-sm text-slate-600">{ctrl.category || "—"}</td>
                <td className="py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold border ${getNetBadgeColor(ctrl.net)}`}>
                    {ctrl.net}
                  </span>
                </td>
                <td className="py-3 text-center">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${getSignalColor(ctrl.signal)}`}>
                    {ctrl.signal}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal.Body>
  </Modal>
);

// =============================================================================
// Main
// =============================================================================

const ControlOverview: React.FC = () => {
  const { allowed: canView, loading: permLoading } = usePermission('CTR_CONTROL_LIBRARY', 'view');
  const { allowed: canModify } = usePermission('CTR_CONTROL_LIBRARY', 'view+modify');
  const [rows, setRows] = useState<ControlRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [selectedControl, setSelectedControl] = useState<ControlRegister | null>(null);
  const [loadingControl, setLoadingControl] = useState(false);

  const handleSelectControl = async (controlId: number) => {
    if (loadingControl) return;
    setLoadingControl(true);
    try {
      const ctrl = await controlRegisterService.getById(controlId);
      setSelectedControl(ctrl);
    } catch (e) {
      swalError("Failed to load control details");
    } finally {
      setLoadingControl(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const controls = await controlRegisterService.getAll({ status: 'ALL' });
        const merged: ControlRow[] = controls
          .filter((c: ControlRegister) => c.is_active === 1)
          .map((c: ControlRegister) => {
            const hasScore = c.display_existence != null && c.display_effectiveness != null;
            const existence     = c.display_existence     ?? 0;
            const effectiveness = c.display_effectiveness ?? 0;
            const net = c.display_net_score ?? existence * effectiveness;
            return {
              id: c.id,
              code: c.control_code,
              name: c.control_name,
              category: c.category_name || "",
              controlType: c.control_type || "",
              existence,
              effectiveness,
              net,
              signal: c.signal || "UNKNOWN",
              hasScore,
            };
          });
        if (!cancelled) setRows(merged);
      } catch (e: any) {
        if (!cancelled) setToast({ type: 'error', message: e?.message || "Failed to load controls" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const scoredRows = useMemo(() => rows.filter(r => r.hasScore), [rows]);

  const insights = useMemo(() => {
    const healthy = rows.filter(r => r.signal === "HEALTHY").length;
    const warning = rows.filter(r => r.signal === "WARNING").length;
    const breach  = rows.filter(r => r.signal === "BREACH").length;

    const catCounts = new Map<string, number>();
    rows.forEach(r => {
      const key = r.category || "Uncategorised";
      catCounts.set(key, (catCounts.get(key) || 0) + 1);
    });
    const categoryCounts = Array.from(catCounts.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);

    const SIGNAL_COLOR: Record<string, string> = {
      HEALTHY: "#10b981", WARNING: "#f59e0b", BREACH: "#ef4444",
    };
    const signalCounts = ["HEALTHY", "WARNING", "BREACH"].map(s => ({
      label: s,
      count: rows.filter(r => r.signal === s).length,
      color: SIGNAL_COLOR[s],
    })).filter(s => s.count > 0);
    // Only scored controls carry a signal — the distribution total must be the
    // sum of the three bands, NOT rows.length (which includes unscored controls
    // and skewed the percentages: 2/5=40% instead of 2/4=50%).
    const signalTotal = healthy + warning + breach;

    return { healthy, warning, breach, categoryCounts, signalCounts, signalTotal };
  }, [rows]);

  const [modalState, setModalState] = useState({
    isOpen: false,
    controls: [] as ControlRow[],
    effectiveness: 0,
    existence: 0,
  });

  // ── Not-authorised gate ─────────────────────────────────────────────────
  if (!permLoading && !canView) {
    return (
      <NoDataFound title="Not authorised" message="You do not have access to 'CTR_INSIGHTS'." />
    );
  }

  return (
    <div className="netbody-card flex flex-col h-full min-h-[500px]">
      <div className="netbody-content">
        <div className="scroll-container-nofilters flex-1 overflow-hidden">

          {loading && <ControlInsightsSkeleton />}

          {!loading && (
            <div className="py-4 px-2">
              <div className="flex gap-4 items-stretch">

                {/* Left: matrix full height */}
                <div className="shrink-0" style={{ width: '390px' }}>
                  <ControlMatrix
                    title="Control Existence × Effectiveness"
                    description={`Net control score (E × X) for ${scoredRows.length} scored controls.`}
                    icon={Shield}
                    rows={scoredRows}
                    onCellClick={(controls: ControlRow[], eff: number, exi: number) =>
                      setModalState({ isOpen: true, controls, effectiveness: eff, existence: exi })}
                  />
                </div>

                {/* Right: KPI row + distributions below */}
                <div className="flex flex-col gap-4 flex-1 min-w-0 min-h-0">

                  {/* KPI cards — single row */}
                  <div className="grid grid-cols-4 gap-4">
                    <KpiCard label="Total Controls" value={rows.length}
                      sub={`${scoredRows.length} scored, ${rows.length - scoredRows.length} unscored`}
                      icon={Shield} iconBg="#f5f3ff" iconColor="#6366f1" />
                    <KpiCard label="Healthy" value={insights.healthy}
                      sub="Signal: Healthy"
                      icon={CheckCircle} iconBg="#ecfdf5" iconColor="#10b981" />
                    <KpiCard label="Warning" value={insights.warning}
                      sub="Signal: Warning"
                      icon={AlertTriangle} iconBg="#fffbeb" iconColor="#f59e0b" />
                    <KpiCard label="Breach" value={insights.breach}
                      sub="Signal: Breach"
                      icon={XCircle} iconBg="#fef2f2" iconColor="#ef4444" />
                  </div>

                  {/* Distributions — side by side, equal height */}
                  <div className="flex gap-4 items-stretch flex-1">
                    <DistributionWidget
                      title="Controls by Category"
                      data={insights.categoryCounts}
                      icon={Briefcase}
                      total={rows.length}
                      accentColor="#6366f1"
                      className="flex-1"
                    />
                    <DistributionWidget
                      title="Signal Distribution"
                      data={insights.signalCounts}
                      icon={Activity}
                      total={insights.signalTotal}
                      accentColor="#10b981"
                      className="flex-1"
                    />
                  </div>

                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ControlDetailModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState(prev => ({ ...prev, isOpen: false }))}
        data={modalState.controls}
        effectiveness={modalState.effectiveness}
        existence={modalState.existence}
        onSelectControl={handleSelectControl}
      />

      {selectedControl && (
        <ControlConfigModal
          isOpen={!!selectedControl}
          onClose={() => setSelectedControl(null)}
          control={selectedControl}
          canModify={canModify}
          canApprove={canModify}
          initialTab="canvas"
        />
      )}

      <span className="hidden"><TrendingUp /><TrendingDown /></span>
      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default ControlOverview;
